import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";
import { SubCategoryData } from "types/subcategory.api";
import {
  deleteSubCategory,
  getSubCategories,
  manageSubCategory,
} from "./thunk";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface SubCategoryStatus {
  fetch: RequestStatus;
  manange: RequestStatus;
  delete: RequestStatus;
}

interface SubCategoryErrors {
  fetch: string | null;
  manange: string | null;
  delete: string | null;
}

export interface SubCategoryState {
  subcategories: SubCategoryData[];
  isLoadingSubCategory: boolean;
  ids: number[];
  pagination: PaginationMeta;
  status: SubCategoryStatus;
  errors: SubCategoryErrors;
}

export const initialState: SubCategoryState = {
  subcategories: [],
  isLoadingSubCategory: false,
  ids: [],
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 0,
    from: 0,
    links: [],
    path: "",
    to: 0,
  },
  status: {
    fetch: "idle",
    manange: "idle",
    delete: "idle",
  },
  errors: {
    fetch: null,
    manange: null,
    delete: null,
  },
};

const subcategoriesSlice = createSlice({
  name: "subcategory",
  initialState,
  reducers: {
    clearSubCategories: (state) => {
      state.subcategories = [];
      state.ids = [];
      state.pagination = { ...initialState.pagination };
      state.status = { ...initialState.status };
      state.errors = { ...initialState.errors };
      state.isLoadingSubCategory = false;
    },
  },
  extraReducers: (builder) => {
    // get Categories
    builder.addCase(getSubCategories.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
      state.isLoadingSubCategory = true;
    });
    builder.addCase(
      getSubCategories.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<SubCategoryData>>>
      ) => {
        console.log(action);
        state.status.fetch = "succeeded";
        state.subcategories = action.payload.payload.data;
        state.ids = action.payload.payload.data.map((invoice) => invoice.id);
        state.isLoadingSubCategory = false;
        state.pagination = action.payload.payload.meta;
      }
    );
    builder.addCase(getSubCategories.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
      state.isLoadingSubCategory = false;
    });

    builder.addCase(manageSubCategory.pending, (state) => {
      state.status.manange = "loading";
      state.errors.manange = null;
    });
    builder.addCase(manageSubCategory.fulfilled, (state, action) => {
      state.status.manange = "succeeded";
      const updatedCategory = action.payload.payload;
      const index = state.subcategories.findIndex(
        (cat) => cat.id === updatedCategory.id
      );
      if (index !== -1) {
        // EDIT sub category
        state.subcategories[index] = updatedCategory;
      } else {
        // ADD new sub category
        state.subcategories.unshift(updatedCategory);
        state.ids.unshift(updatedCategory.id);
        if (state.subcategories.length > state.pagination.per_page) {
          state.pagination.to--;
          state.subcategories.pop();
        }
        state.pagination.to++;
        state.pagination.total++;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    });
    builder.addCase(manageSubCategory.rejected, (state, action) => {
      state.status.manange = "failed";
      state.errors.manange = action.payload || null;
    });

    // delete Category
    builder.addCase(deleteSubCategory.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteSubCategory.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedSubCategoryId = action.payload.payload;
        state.subcategories = state.subcategories.filter(
          (subcat) => subcat.id !== deletedSubCategoryId
        );
        state.ids = state.ids.filter((id) => id !== deletedSubCategoryId);
        state.pagination.to--;
        state.pagination.total--;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    );
    builder.addCase(deleteSubCategory.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });
  },
});

export default subcategoriesSlice.reducer;

export const { clearSubCategories } = subcategoriesSlice.actions;

export const selectSubCategoriesState = (state: any) =>
  state.subcategory as typeof initialState;
